// /src/pages/EditVendor.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import {
  useGetVendorByIdQuery,
  useUpdateVendorMutation,
  useActivateVendorMutation,
  useDeactivateVendorMutation,
} from "../toolkitServices/vendorApi";
import { indianStates } from "../utils/helpers";

/* Shared UI tokens */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

/* Field groups for step validations */
const STEP_FIELDS = {
  1: [
    "vendorName",
    "companyName",
    "email",
    "contact",
    "adhar",
    "pan",
    "gst",
    "shortName",
  ],
  2: [
    "vendorType",
    "rateOfInterest",
    "creditPeriod",
    "advanceAmount",
    "balanceAmount",
    "fineGold",
    "fineSilver",
  ],
  3: ["addressLine1", "city", "pincode", "state", "country", "isActive"],
};

const EditVendor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [step, setStep] = useState(1);

  // Queries & Mutations
  const { data, isFetching, isError, error } = useGetVendorByIdQuery(id);
  const [updateVendor, { isLoading: isSaving }] = useUpdateVendorMutation();
  const [activateVendor] = useActivateVendorMutation();
  const [deactivateVendor] = useDeactivateVendorMutation();

  const defaultValues = useMemo(
    () => ({
      vendorName: "",
      companyName: "",
      email: "",
      contact: "",
      adhar: "",
      pan: "",
      gst: "",
      shortName: "",
      vendorType: "party",
      rateOfInterest: "",
      creditPeriod: "",
      advanceAmount: "",
      balanceAmount: "",
      fineGold: "",
      fineSilver: "",
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",
      isActive: true,
    }),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting },
    setFocus,
    watch,
  } = useForm({
    defaultValues,
    shouldUnregister: false,
    mode: "onBlur",
  });

  // Prefill when data arrives
  useEffect(() => {
    const doc = data?.data || data;
    if (doc) {
      reset({
        vendorName: doc.vendorName || "",
        companyName: doc.companyName || "",
        email: doc.email || "",
        contact: doc.contact || "",
        adhar: doc.adhar || "",
        pan: doc.pan || "",
        gst: doc.gst || "",
        shortName: doc.shortName || "",
        vendorType: doc.vendorType || "party",
        rateOfInterest: doc.rateOfInterest || "",
        creditPeriod: doc.creditPeriod || "",
        advanceAmount: doc.advanceAmount || "",
        balanceAmount: doc.balanceAmount || "",
        fineGold: doc.fineGold || "",
        fineSilver: doc.fineSilver || "",
        addressLine1: doc.addressLine1 || "",
        city: doc.city || "",
        pincode: doc.pincode || "",
        state: doc.state || "Maharashtra",
        country: doc.country || "india",
        isActive: typeof doc.isActive === "boolean" ? doc.isActive : true,
      });
      requestAnimationFrame(() => setFocus("vendorName"));
    }
  }, [data, reset, setFocus]);

  // Errors while loading vendor
  useEffect(() => {
    if (isError && error) {
      const msg =
        error?.data?.msg ||
        error?.data?.message ||
        error?.error ||
        "Failed to load vendor";
      toast.error(msg);
    }
  }, [isError, error]);

  // Step focus
  useEffect(() => {
    const idRaf = requestAnimationFrame(() => {
      if (step === 1) setFocus("vendorName");
      if (step === 2) setFocus("vendorType");
      if (step === 3) setFocus("addressLine1");
    });
    return () => cancelAnimationFrame(idRaf);
  }, [step, setFocus]);

  // Keyboard shortcuts
  const nextStep = useCallback(async () => {
    const ok = await trigger(STEP_FIELDS[step]);
    if (ok) setStep((s) => (s < 3 ? s + 1 : s));
  }, [step, trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 3) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (isBack) {
        e.preventDefault();
        navigate("/members/vendor-list");
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, handleSubmit, nextStep, prevStep, navigate]);

  // Toggle Active instantly (server)
  const isActive = watch("isActive");
  const onToggleActive = async () => {
    try {
      if (isActive) {
        await deactivateVendor(id).unwrap();
        reset({ ...watch(), isActive: false });
        toast.success("Vendor deactivated");
      } else {
        await activateVendor(id).unwrap();
        reset({ ...watch(), isActive: true });
        toast.success("Vendor activated");
      }
    } catch (e) {
      toast.error(e?.data?.msg || e?.data?.message || "Action failed");
    }
  };

  // Submit
  const onSubmit = async (form) => {
    const payload = {
      vendorName: (form.vendorName || "").trim(),
      companyName: (form.companyName || "").trim(),
      email: (form.email || "").trim().toLowerCase(),
      contact: (form.contact || "").trim(),
      adhar: (form.adhar || "").trim(),
      pan: (form.pan || "").trim().toUpperCase(),
      gst: (form.gst || "").trim().toUpperCase(),
      shortName: (form.shortName || "").trim().toUpperCase(),
      vendorType: form.vendorType || "party",
      rateOfInterest: (form.rateOfInterest || "").trim(),
      creditPeriod: (form.creditPeriod || "").trim(),
      advanceAmount: (form.advanceAmount || "").trim(),
      balanceAmount: (form.balanceAmount || "").trim(),
      fineGold: (form.fineGold || "").trim(),
      fineSilver: (form.fineSilver || "").trim(),
      addressLine1: (form.addressLine1 || "").trim(),
      city: (form.city || "").trim(),
      pincode: (form.pincode || "").trim(),
      state: (form.state || "").trim(),
      country: (form.country || "").trim(),
      isActive: !!form.isActive,
    };

    try {
      await updateVendor({ id, ...payload }).unwrap();
      toast.success("Vendor updated!");
      navigate("/members/vendor-list");
    } catch (e) {
      const msg =
        e?.data?.msg || e?.data?.message || e?.error || "Update failed";
      toast.error(msg);
    }
  };

  const loading = isSaving || isSubmitting || isFetching;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/members/vendor-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Vendor List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                  Edit Vendor
                </h1>
                <p className="text-xs text-gray-500">
                  Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd> save/next,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> back,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">Alt</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev step
                </p>
              </div>

              <div className="flex items-center gap-2 pr-2">
                <span className="hidden sm:inline text-xs text-gray-500">Step {step} of 3</span>
                <button
                  type="button"
                  onClick={onToggleActive}
                  className={`px-3 py-1.5 text-xs rounded border ${
                    isActive
                      ? "border-red-400 text-red-600 hover:bg-red-50"
                      : "border-green-500 text-green-600 hover:bg-green-50"
                  }`}
                >
                  {isActive ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() =>
          requestAnimationFrame(() =>
            setFocus(step === 1 ? "vendorName" : step === 2 ? "vendorType" : "addressLine1")
          )
        }
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vendor Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("vendorName", { required: "Vendor name is required" })}
                  aria-invalid={!!errors.vendorName}
                  placeholder="Enter vendor name"
                  autoComplete="off"
                  className={`${baseInput} ${errors.vendorName ? errRing : okRing}`}
                />
                {errors.vendorName?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.vendorName.message}</p>
                )}
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("companyName", { required: "Company name is required" })}
                  aria-invalid={!!errors.companyName}
                  placeholder="Enter company name"
                  autoComplete="off"
                  className={`${baseInput} ${errors.companyName ? errRing : okRing}`}
                />
                {errors.companyName?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.companyName.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">Email</label>
                <input
                  {...register("email", {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email",
                    },
                  })}
                  aria-invalid={!!errors.email}
                  placeholder="Enter email"
                  autoComplete="off"
                  className={`${baseInput} ${errors.email ? errRing : okRing}`}
                />
                {errors.email?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Contact */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Contact <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("contact", {
                    required: "Contact is required",
                    validate: (v) => /^\d{10}$/.test(v) || "Contact must be exactly 10 digits",
                  })}
                  aria-invalid={!!errors.contact}
                  placeholder="Enter contact"
                  autoComplete="off"
                  className={`${baseInput} ${errors.contact ? errRing : okRing}`}
                />
                {errors.contact?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.contact.message}</p>
                )}
              </div>

              {/* Aadhar */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">Aadhar</label>
                <input
                  {...register("adhar", {
                    validate: (v) =>
                      v === "" || /^\d{12}$/.test(v) || "Aadhar must be a 12-digit number",
                  })}
                  aria-invalid={!!errors.adhar}
                  placeholder="Enter aadhar"
                  autoComplete="off"
                  className={`${baseInput} ${errors.adhar ? errRing : okRing}`}
                />
                {errors.adhar?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.adhar.message}</p>
                )}
              </div>

              {/* PAN */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">PAN</label>
                <input
                  {...register("pan", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test((v || "").toUpperCase()) ||
                      "PAN must be in format AAAAA9999A",
                  })}
                  aria-invalid={!!errors.pan}
                  placeholder="Enter PAN"
                  autoComplete="off"
                  className={`${baseInput} ${errors.pan ? errRing : okRing}`}
                />
                {errors.pan?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.pan.message}</p>
                )}
              </div>

              {/* GST */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">GST</label>
                <input
                  {...register("gst", {
                    validate: (v) =>
                      v === "" ||
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        (v || "").toUpperCase()
                      ) ||
                      "GST number must be a valid 15-character alphanumeric code",
                  })}
                  aria-invalid={!!errors.gst}
                  placeholder="Enter GST"
                  autoComplete="off"
                  className={`${baseInput} ${errors.gst ? errRing : okRing}`}
                />
                {errors.gst?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.gst.message}</p>
                )}
              </div>

              {/* Short Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Short Name
                </label>
                <input
                  {...register("shortName")}
                  placeholder="Enter short name (e.g. VND01)"
                  autoComplete="off"
                  className={`${baseInput} ${okRing}`}
                />
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vendor Type */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Vendor Type <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("vendorType", { required: "Vendor type is required" })}
                  aria-invalid={!!errors.vendorType}
                  className={`${baseInput} ${errors.vendorType ? errRing : okRing}`}
                >
                  <option value="party">Party</option>
                  <option value="supplier">Supplier</option>
                  <option value="other">Other</option>
                </select>
                {errors.vendorType?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.vendorType.message}</p>
                )}
              </div>

              {/* Rate of Interest */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Rate of Interest
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register("rateOfInterest")}
                  placeholder="Enter rate of interest"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Credit Period */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Credit Period
                </label>
                <input
                  type="number"
                  {...register("creditPeriod")}
                  placeholder="Enter credit period"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Advance Amount */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Advance Amount
                </label>
                <input
                  {...register("advanceAmount")}
                  placeholder="Enter advance amount"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Balance Amount */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Balance Amount
                </label>
                <input
                  {...register("balanceAmount")}
                  placeholder="Enter balance amount"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Fine Gold */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Fine Gold
                </label>
                <input
                  {...register("fineGold")}
                  placeholder="Enter fine gold"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Fine Silver */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Fine Silver
                </label>
                <input
                  {...register("fineSilver")}
                  placeholder="Enter fine silver"
                  className={`${baseInput} ${okRing}`}
                />
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Address Line 1 */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Address Line 1
                </label>
                <input
                  {...register("addressLine1")}
                  placeholder="Enter street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  {...register("city")}
                  placeholder="Enter city"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  {...register("pincode", {
                    validate: (v) => v === "" || /^\d{6}$/.test(v) || "Pincode must be 6 digits",
                  })}
                  aria-invalid={!!errors.pincode}
                  placeholder="Enter pincode"
                  className={`${baseInput} ${errors.pincode ? errRing : okRing}`}
                />
                {errors.pincode?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.pincode.message}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("state", { required: "State is required" })}
                  aria-invalid={!!errors.state}
                  className={`${baseInput} ${errors.state ? errRing : okRing}`}
                >
 <option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}                </select>
                {errors.state?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.state.message}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("country", { required: "Country is required" })}
                  aria-invalid={!!errors.country}
                  className={`${baseInput} ${errors.country ? errRing : okRing}`}
                >
                  <option value="india">India</option>
                </select>
                {errors.country?.message && (
                  <p className="text-xs text-red-600 mt-1">{errors.country.message}</p>
                )}
              </div>

              {/* Active flag (client-side reflect) */}
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" {...register("isActive")} />
                  <span className="text-sm text-[#4b3d91] font-medium">Active</span>
                </label>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step < 3) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
              title={step === 3 ? "Update (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {loading ? "Updating..." : step === 3 ? "Update (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/members/vendor-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditVendor;
